/**
 * Packs dashboard cards so a tall one doesn't leave a hole beside it.
 *
 * Cards used to flow like words in a paragraph: they filled a line, then
 * wrapped to the next one *below the tallest card in the line*. Stretch the
 * to-do list down the page and everything after it started underneath the
 * bottom of it, leaving a screen of empty wallpaper.
 *
 * This places each card at the highest position it will fit, scanning left to
 * right — so short cards stack up the left while a long one runs down the
 * right. It is the "bottom-left fill" heuristic. Not optimal packing, which
 * is NP-hard, but with seven cards the difference is theoretical and this has
 * the property that actually matters: it is stable and predictable. Move a
 * card and it goes where you'd expect, rather than the whole arrangement
 * reshuffling itself.
 *
 * Order is respected: earlier cards are placed first and never move out of
 * the way of a later one. Dragging to reorder still means what it says.
 */

export interface PackItem {
  key: string;
  /** The card's chosen width. Clamped to the container. */
  width: number;
  /** Measured, including its border. */
  height: number;
}

export interface Placement {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PackResult {
  placements: Placement[];
  /** How tall the container has to be to hold them all. */
  height: number;
}

/** Do two horizontal spans overlap at all? */
function overlaps(aX: number, aW: number, bX: number, bW: number): boolean {
  return aX < bX + bW && bX < aX + aW;
}

export function packCards(items: PackItem[], containerWidth: number, gap: number): PackResult {
  const placements: Placement[] = [];

  for (const item of items) {
    const width = Math.min(item.width, containerWidth);

    /*
     * Only two kinds of x are ever worth trying: hard against the left edge,
     * or hard against the right edge of something already placed. Any other
     * position is the same or worse, and would leave a sliver of unusable
     * space beside it.
     */
    const candidates = [0, ...placements.map((p) => p.x + p.width + gap)]
      .filter((x, i, all) => all.indexOf(x) === i)
      // A card may not hang off the right-hand edge. The rounding tolerance
      // matters: widths come from measured pixels and are rarely whole.
      .filter((x) => x + width <= containerWidth + 0.5)
      .sort((a, b) => a - b);

    // There is always somewhere to put it — the width is clamped to the
    // container, so x = 0 always qualifies.
    let best = { x: 0, y: 0 };
    let first = true;

    for (const x of candidates) {
      // Rest on top of the lowest thing this card would sit over.
      let y = 0;
      for (const p of placements) {
        if (overlaps(x, width, p.x, p.width)) y = Math.max(y, p.y + p.height + gap);
      }

      // Higher wins; at equal height, further left wins. That tie-break is
      // what keeps the arrangement looking deliberate rather than scattered.
      if (first || y < best.y - 0.5 || (Math.abs(y - best.y) <= 0.5 && x < best.x)) {
        best = { x, y };
        first = false;
      }
    }

    placements.push({ key: item.key, x: best.x, y: best.y, width, height: item.height });
  }

  const height = placements.reduce((tallest, p) => Math.max(tallest, p.y + p.height), 0);

  return { placements, height };
}

/* --------------------------------------------------------------- tidying */

export interface GridItem {
  key: string;
  /** How many columns this card would like. Clamped to the column count. */
  span: number;
  height: number;
}

export interface GridResult extends PackResult {
  columns: number;
  columnWidth: number;
}

/**
 * "Tidy up": everything on a grid, evenly spaced, sized to the screen.
 *
 * The old tidy kept each card's hand-dragged width and packed those, which
 * meant the result was as ragged as whatever it started from — cards half a
 * column wide, gaps that no card fits into, nothing lining up with anything.
 * A teacher pressing a button called "Tidy up" wants columns.
 *
 * So the width is thrown away and rebuilt: a column count that suits the
 * screen, every card an exact number of columns wide, and the same gap
 * everywhere. Heights are still the cards' own, because a to-do list and a
 * weather card are not the same height and pretending otherwise wastes the
 * page.
 */
export function gridColumns(containerWidth: number, gap: number, target = 340): number {
  /*
   * Between one and six columns of roughly 340px.
   *
   * The lower bound is a phone; the upper is a 27-inch monitor, where four
   * columns would make every card 600px wide and a weather card 600px wide is
   * mostly whitespace. Six is where it stops because there are ten cards.
   */
  const raw = Math.round((containerWidth + gap) / (target + gap));
  return Math.max(1, Math.min(6, raw));
}

export function gridLayout(items: GridItem[], containerWidth: number, gap: number): GridResult {
  const columns = gridColumns(containerWidth, gap);
  const columnWidth = (containerWidth - gap * (columns - 1)) / columns;

  const packed = packCards(
    items.map((item) => {
      const span = Math.max(1, Math.min(columns, Math.round(item.span)));
      return { key: item.key, width: columnWidth * span + gap * (span - 1), height: item.height };
    }),
    containerWidth,
    gap,
  );

  /*
   * Packing already lands cards on column boundaries — every candidate x is
   * either zero or the right edge of a card that is itself a whole number of
   * columns. Snapping anyway costs nothing and makes that guarantee explicit
   * rather than emergent, so a future change to the packer cannot quietly
   * break the alignment.
   */
  const placements = packed.placements.map((p) => ({
    ...p,
    x: Math.round(p.x / (columnWidth + gap)) * (columnWidth + gap),
  }));

  return { ...packed, placements, columns, columnWidth };
}

/* --------------------------------------------------------------- growing */

export interface MovableCard {
  key: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Nothing may sit on top of anything else.
 *
 * Two ways a board ends up overlapping, and this handles both with the same
 * pass. A card grows while you are looking at it — add a to-do and it gets
 * taller — and a card is *already* taller than it was when the arrangement
 * was saved, which is what happens when you add to-dos on one computer and
 * then open the dashboard on another. The second case has no growth event to
 * watch for: the card is simply the wrong size the moment it first appears.
 *
 * Cards are taken in reading order and only ever moved down, so the
 * arrangement is preserved rather than re-flowed — what was in the left
 * column stays in the left column. Locked cards move too: lock means "do not
 * let me nudge this by accident", not "let something be drawn over it".
 */
export function resolveOverlaps(cards: MovableCard[], gap: number): Record<string, number> {
  const y = new Map(cards.map((c) => [c.key, c.y]));

  // A fixpoint, because pushing one card down can land it on another that was
  // above it a moment ago. Capped: a pass that cannot settle must stop rather
  // than freeze the page.
  for (let pass = 0; pass < cards.length + 2; pass++) {
    let moved = false;

    const order = [...cards].sort((a, b) => {
      const dy = y.get(a.key)! - y.get(b.key)!;
      return Math.abs(dy) > 0.5 ? dy : a.x - b.x;
    });

    for (let i = 1; i < order.length; i++) {
      const card = order[i]!;

      for (let j = 0; j < i; j++) {
        const above = order[j]!;
        if (!overlaps(above.x, above.width, card.x, card.width)) continue;

        const top = y.get(card.key)!;
        const bottom = y.get(above.key)! + above.height;
        // Only a real collision counts. A card that already clears the one
        // above it by the gap is where its owner put it.
        if (top + 0.5 >= bottom + gap) continue;
        if (top + card.height + 0.5 <= y.get(above.key)!) continue;

        y.set(card.key, bottom + gap);
        moved = true;
      }
    }

    if (!moved) break;
  }

  const changed: Record<string, number> = {};
  for (const card of cards) {
    if (Math.abs(y.get(card.key)! - card.y) > 0.5) changed[card.key] = y.get(card.key)!;
  }

  return changed;
}

/**
 * A card shrank; whatever was resting on it comes back up.
 *
 * The mirror of the overlap pass, and it cannot be done the same way. Pushing
 * down is forced — two cards in the same place is always wrong, so no history
 * is needed to know it must be fixed. Pulling up is a choice: a gap under a
 * card is only wrong if that gap is there *because* the card used to be
 * taller. A card the teacher deliberately parked halfway down an empty column
 * is exactly where she wants it, and hoisting it to the top would be the
 * dashboard rearranging itself behind her back.
 *
 * So the rule is flushness. A card that was sitting exactly one gap under the
 * one that shrank was resting on it, and follows it up by the same amount;
 * anything resting on *that* follows too. Everything else stays put. Because
 * the whole chain moves by one delta, what was flush stays flush and the
 * relationship survives repeated adds and deletes.
 *
 * `previousHeight` is the height the card had a moment ago; `cards` carry the
 * new one.
 */
export function pullUpFlush(
  cards: MovableCard[],
  shrunkKey: string,
  previousHeight: number,
  gap: number,
): Record<string, number> {
  const shrunk = cards.find((c) => c.key === shrunkKey);
  if (!shrunk) return {};

  const delta = previousHeight - shrunk.height;
  if (delta <= 0.5) return {};

  const moved: Record<string, number> = {};
  // Each entry: a card, and the bottom edge it had before anything moved.
  const queue: { key: string; oldBottom: number }[] = [
    { key: shrunkKey, oldBottom: shrunk.y + previousHeight },
  ];
  const seen = new Set<string>([shrunkKey]);

  while (queue.length > 0) {
    const { key, oldBottom } = queue.shift()!;
    const source = cards.find((c) => c.key === key)!;

    for (const card of cards) {
      if (seen.has(card.key)) continue;
      if (!overlaps(source.x, source.width, card.x, card.width)) continue;
      // Resting on it — not merely below it somewhere.
      if (Math.abs(card.y - (oldBottom + gap)) > 1) continue;

      seen.add(card.key);
      moved[card.key] = card.y - delta;
      queue.push({ key: card.key, oldBottom: card.y + card.height });
    }
  }

  return moved;
}
